import type { SelectEventArgs } from '@syncfusion/ej2-react-schedule'
import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { TIME_FORMAT } from 'common/constants/timeFormat'
import { useAppDispatch } from 'common/hooks/redux'
import { UtilService } from 'common/services/utilService'
import { add, compareAsc, differenceInMinutes, format, isAfter } from 'date-fns'
import dayjs from 'dayjs'
import { UPDATE_INFO_DRAGGING } from 'features/Home/Book/state/slice/bookSlice'
import { COUNT_AS_OVERLAPPING, ROOM_STATUS } from 'features/Home/constants/infoConstants'
import type { IPatientRoom } from 'features/Home/interfaces/IInfoPatient'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { InfoService } from 'features/Home/services/infoService'
import { useCallback, useMemo, useState } from 'react'
import { buildScheduleUpdatePayload, snapToFiveMinutes } from './helpers'
import { mapSchedulesToEvents } from './mapSchedulesToEvents'
import { safeRefreshScheduleEvents } from './safeRefreshScheduleEvents'

type UseScheduleCalendarResponse = {
  awaitConfirmationEvent: any
  setAwaitConfirmationEvent: (event: any) => void
  handleScheduleChange: (data: any) => Promise<void>
  proceedToUpdateEvents: () => Promise<void>
  events: IPatientSchedule[]
  handleSelect: (props: any) => void
}

type UseScheduleCalendarProps = {
  dayInterval: { min: string; max: string }
  disabledEverything?: boolean
  disabledIntervals: any[]
  formattedDate: string
  isUpdating: boolean
  rooms: IPatientRoom[]
  schedules: IPatientSchedule[]
  timeZone: string
  scheduleObj: any
  prepopulateTimeOnClick: number
  handleUpdateEvents: (adjustedData: any, event: any) => Promise<boolean>
  createCustomAppointment?: (start: Date, end: Date, resourceId: string) => void
}

export const useScheduleCalendar = ({
  dayInterval,
  disabledEverything = false,
  disabledIntervals,
  formattedDate,
  isUpdating,
  rooms,
  schedules,
  timeZone,
  scheduleObj,
  prepopulateTimeOnClick,
  createCustomAppointment,
  handleUpdateEvents,
}: UseScheduleCalendarProps): UseScheduleCalendarResponse => {
  const [awaitConfirmationEvent, setAwaitConfirmationEvent] = useState<any>(null)

  const dispatch = useAppDispatch()

  const { setNotification } = useNotification()

  const isBetween = (number: number, lowerBound: number, upperBound: number): boolean => {
    return number >= lowerBound && number <= upperBound
  }

  const updateDragging = (value: boolean) => {
    dispatch(UPDATE_INFO_DRAGGING(value))
  }

  const shouldProceedToUpdate = ({
    resourceId,
    start,
    end,
    event,
    room,
    isResizing,
  }: {
    resourceId: string
    start: any
    end: any
    event: any
    room: IPatientRoom
    isResizing?: boolean
    isDrag?: boolean
  }) => {
    const eventsFromRoom = schedules.filter(
      (item) =>
        item.room.code === resourceId &&
        item.id !== event.id &&
        COUNT_AS_OVERLAPPING.includes(item.status.code),
    )

    const { nrOfOverlappingEvents, overlappingEvents } =
      InfoService.getNumberOfOverlappingIntervals({ start, end }, eventsFromRoom, resourceId)

    if (!isBetween(nrOfOverlappingEvents, 1, room.max_patients || 1) && !room?.is_waiting_room) {
      setNotification({
        title: 'Update schedule error',
        description: `You reached the max number of ${
          room.max_patients || 1
        } patients at the same time for this room.`,
        type: ALERT_CONSTANTS.ERROR,
      })
      return false
    }

    const shouldApplyRoomRule = !InfoService.checkIfCurrentDateOverlapDisabledIntervals(
      disabledIntervals,
      room,
      dayInterval,
      timeZone,
    )
    if (
      shouldApplyRoomRule &&
      room &&
      room.status &&
      room.status.code !== ROOM_STATUS.FREE &&
      room.status.code !== ROOM_STATUS.CLEANUP &&
      room.status.code !== ROOM_STATUS.BUSY &&
      !isResizing
    ) {
      return false
    }

    const { start_time: event_start, end_time: event_end, room_id: eventRoom } = event
    const { overlappingEvents: overlappingEventsBeforeUpdate } =
      InfoService.getNumberOfOverlappingIntervals(
        { start: event_start, end: event_end },
        eventsFromRoom,
        eventRoom,
      )

    const shouldShowOverlappingEventsNotification =
      nrOfOverlappingEvents > 1 &&
      !UtilService.arrDeepEqual(overlappingEventsBeforeUpdate, overlappingEvents) &&
      room.value !== rooms[rooms.length - 1].value

    if (shouldShowOverlappingEventsNotification) {
      setAwaitConfirmationEvent({
        start,
        end,
        event,
        room,
      })

      return false
    }

    return true
  }

  const revertScheduleChange = useCallback(() => {
    safeRefreshScheduleEvents(scheduleObj?.current)
  }, [scheduleObj])

  const handleScheduleChange = async (data: IPatientSchedule): Promise<void> => {
    updateDragging(false)

    if (isUpdating || disabledEverything) {
      revertScheduleChange()
      return
    }

    const start = snapToFiveMinutes(new Date(data.start_time))
    let end = snapToFiveMinutes(new Date(data.end_time))

    const originalEvent = schedules.find((event) => event.id === data.id)
    const isResizing = originalEvent
      ? originalEvent.start_time === data.start_time || originalEvent.end_time === data.end_time
      : false

    const durationInMinutes = differenceInMinutes(end, start)
    if (durationInMinutes < 5) {
      end = dayjs(end).add(5, 'minutes').toDate()
    }

    const [label, value] = data.room_id.split('/')
    const room = rooms.find((room: IPatientRoom) => room.label === label && room.value === value)
    const shouldUpdateEvent = shouldProceedToUpdate({
      resourceId: data.room_id,
      start,
      end,
      event: data,
      room,
      isResizing,
    })

    if (!shouldUpdateEvent) {
      revertScheduleChange()
      return
    }

    const { nrOfOverlappingEvents } = InfoService.getNumberOfOverlappingIntervals(
      { start, end },
      schedules,
      data.room.code,
    )

    if (nrOfOverlappingEvents > 1) {
      setAwaitConfirmationEvent({ start, end, event: data, room })
      revertScheduleChange()
      return
    }

    const adjustedData = buildScheduleUpdatePayload(data, {
      room: room?.value,
      start_time: format(start, TIME_FORMAT.TIME_PICKER_HH_SS),
      end_time: format(end, TIME_FORMAT.TIME_PICKER_HH_SS),
    })

    try {
      await handleUpdateEvents(adjustedData, data)
    } catch {
      revertScheduleChange()
    }
  }

  const proceedToUpdateEvents = useCallback(async () => {
    if (!awaitConfirmationEvent) {
      return
    }

    const { start, end, event, room } = awaitConfirmationEvent
    const adjustedData = buildScheduleUpdatePayload(event, {
      room: room?.value,
      start_time: format(start, TIME_FORMAT.TIME_PICKER_HH_SS),
      end_time: format(end, TIME_FORMAT.TIME_PICKER_HH_SS),
    })

    const successUpdate = await handleUpdateEvents(adjustedData, event)
    if (!successUpdate) {
      return
    }

    scheduleObj?.current?.saveEvent({ ...event })
    setAwaitConfirmationEvent(null)
  }, [awaitConfirmationEvent, handleUpdateEvents])

  const eventsToShow = useMemo(
    () =>
      mapSchedulesToEvents(
        schedules,
        timeZone,
        (event) => `${event.room?.label}/${event.room?.value}`,
      ),
    [schedules, timeZone],
  )

  const handleSelect = useCallback(
    (props: SelectEventArgs) => {
      const cellData = props.data as
        | { room_id: string; start_time: Date; end_time: Date }
        | undefined
      if (disabledEverything || !cellData) {
        return
      }

      if ((props.event as Event)?.type === 'mouseleave' && !cellData.start_time) {
        return
      }

      const [label, value] = cellData.room_id.split('/')
      const room = rooms.find((room: IPatientRoom) => room.label === label && room.value === value)

      if (room?.add_patient === 0) {
        return
      }

      const { start_time, room_id } = cellData
      let { end_time } = cellData

      const diffInMinutes = differenceInMinutes(end_time, start_time)
      const prepPopulateTime = prepopulateTimeOnClick
      const updatedEnd = add(start_time, { minutes: prepPopulateTime })
      const disabledIntervalsForRoom = disabledIntervals
        .filter((interval: any) => interval.resourceId === room_id)
        .sort((a: any, b: any) => compareAsc(a.start, b.start))

      const firstDisabledInterval = disabledIntervalsForRoom.find(
        (interval: any) =>
          isAfter(interval.start, start_time) && isAfter(updatedEnd, interval.start),
      )

      const endDayDate = new Date(`${formattedDate} ${dayInterval.max}`)

      const dateToCheck = firstDisabledInterval?.start || endDayDate

      if (diffInMinutes < prepPopulateTime && !isAfter(updatedEnd, dateToCheck)) {
        end_time = updatedEnd
      } else if (diffInMinutes < prepPopulateTime && isAfter(updatedEnd, dateToCheck)) {
        end_time = dateToCheck
      }

      if (createCustomAppointment) {
        createCustomAppointment(start_time, end_time, room_id)
      }
    },
    [disabledEverything, prepopulateTimeOnClick, rooms, disabledIntervals, formattedDate],
  )

  return {
    handleScheduleChange,
    events: eventsToShow,
    awaitConfirmationEvent,
    setAwaitConfirmationEvent,
    proceedToUpdateEvents,
    handleSelect,
  }
}
